import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckSquare, ChevronRight, AlertTriangle, Clock, CheckCircle2, Circle } from 'lucide-react'
import api from '../../services/api'

const RED    = '#FF3B5C'
const ORANGE = '#FF9500'
const BLUE   = '#0A84FF'
const TEAL   = '#00D4AA'
const DIM    = 'rgba(255,255,255,0.3)'

const PRIORITY_COLOR: Record<string, string> = {
  immediate:   RED,
  short_term:  ORANGE,
  medium_term: BLUE,
  systemic:    '#BF5AF2',
}

// Status cycle: open → in_progress → closed
const NEXT_STATUS: Record<string, string> = {
  open:        'in_progress',
  in_progress: 'closed',
  closed:      'open',
}
const STATUS_META: Record<string, { label: string; color: string; Icon: any }> = {
  open:        { label: 'Open',        color: RED,    Icon: Circle },
  in_progress: { label: 'In Progress', color: ORANGE, Icon: Clock },
  overdue:     { label: 'Overdue',     color: RED,    Icon: AlertTriangle },
  closed:      { label: 'Closed',      color: TEAL,   Icon: CheckCircle2 },
  verified:    { label: 'Verified',    color: TEAL,   Icon: CheckCircle2 },
}

const FILTERS = [
  ['my',      'My Actions'],
  ['open',    'Open'],
  ['overdue', 'Overdue'],
  ['all',     'All'],
]

export default function MobileCorrectiveActions() {
  const navigate = useNavigate()
  const [actions, setActions]   = useState<any[]>([])
  const [total, setTotal]       = useState(0)
  const [loading, setLoading]   = useState(true)
  const [filter, setFilter]     = useState('my')
  const [updating, setUpdating] = useState<string | null>(null)

  const load = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({ limit: '100' })
      if (filter === 'overdue') params.set('status', 'overdue')
      else if (filter === 'open') params.set('status', 'open')
      else if (filter === 'my') params.set('assigned_to_me', '1')
      const { data } = await api.get(`/corrective-actions?${params}`)
      setActions(data.data || [])
      setTotal(data.total || 0)
    } catch {}
    setLoading(false)
  }

  useEffect(() => { load() }, [filter])

  const cycleStatus = async (action: any) => {
    const next = NEXT_STATUS[action.status] || 'open'
    setUpdating(action.id)
    try {
      await api.patch(`/corrective-actions/${action.id}`, { status: next })
      setActions(prev => prev.map(a => a.id === action.id ? { ...a, status: next } : a))
    } catch {}
    setUpdating(null)
  }

  // Group by status priority
  const overdue    = actions.filter(a => a.status === 'overdue')
  const open       = actions.filter(a => a.status === 'open')
  const inProgress = actions.filter(a => a.status === 'in_progress')
  const done       = actions.filter(a => ['closed', 'verified'].includes(a.status))

  const groups = [
    { label: 'Overdue', color: RED,    items: overdue },
    { label: 'Open',    color: ORANGE, items: open },
    { label: 'In Progress', color: BLUE, items: inProgress },
    { label: 'Done',    color: TEAL,   items: done },
  ].filter(g => g.items.length > 0)

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100%', paddingBottom: 96 }}>
      {/* Header */}
      <div className="sticky top-0 z-10" style={{ background: 'var(--bg)', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '16px 20px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#FFFFFF', margin: 0 }}>Actions</h1>
          <span style={{ fontSize: 12, fontWeight: 500, color: DIM }}>{total} total</span>
        </div>
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', scrollbarWidth: 'none' }}>
          {FILTERS.map(([val, label]) => {
            const active = filter === val
            return (
              <button key={val} onClick={() => setFilter(val)} style={{
                padding: '6px 14px', borderRadius: 999, fontSize: 12, fontWeight: 600, flexShrink: 0,
                border: 'none', cursor: 'pointer',
                background: active ? ORANGE : 'rgba(255,255,255,0.08)',
                color: active ? '#FFFFFF' : 'rgba(255,255,255,0.45)',
                transition: 'all 150ms',
              }}>
                {label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 20 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '64px 0', color: DIM }}>Loading…</div>
        ) : actions.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '80px 0', gap: 12 }}>
            <div style={{ width: 60, height: 60, borderRadius: '50%', background: `${TEAL}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckSquare size={26} color={TEAL} />
            </div>
            <p style={{ fontWeight: 600, fontSize: 15, color: '#FFFFFF', margin: 0 }}>All clear</p>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', margin: 0 }}>No actions in this view</p>
          </div>
        ) : groups.map(group => (
          <div key={group.label}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: group.color }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                {group.label}
              </span>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)' }}>{group.items.length}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {group.items.map(action => {
                const meta     = STATUS_META[action.status] || STATUS_META.open
                const priColor = PRIORITY_COLOR[action.priority] || DIM
                const isUpdating = updating === action.id
                const daysOverdue = action.due_date
                  ? Math.floor((Date.now() - new Date(action.due_date).getTime()) / 86400000)
                  : null

                return (
                  <div key={action.id} style={{ background: 'var(--white)', borderRadius: 16, overflow: 'hidden', borderLeft: `3px solid ${meta.color}` }}>
                    <div style={{ padding: '13px 14px 12px 14px' }}>
                      {/* Priority + title */}
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                        <button
                          onClick={() => cycleStatus(action)}
                          disabled={isUpdating}
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', flexShrink: 0, marginTop: 1, opacity: isUpdating ? 0.4 : 1 }}
                          title={`Mark as ${NEXT_STATUS[action.status] || 'open'}`}
                        >
                          <meta.Icon size={18} color={meta.color} />
                        </button>
                        <p style={{ fontSize: 13, fontWeight: 600, color: '#FFFFFF', margin: 0, lineHeight: 1.4, flex: 1 }}>
                          {action.title || action.description?.slice(0, 80)}
                        </p>
                        <button
                          onClick={() => navigate(`/m/corrective-actions/${action.id}`)}
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', flexShrink: 0 }}
                        >
                          <ChevronRight size={16} color={DIM} />
                        </button>
                      </div>

                      {/* Meta */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 11, color: 'rgba(255,255,255,0.3)', flexWrap: 'wrap' }}>
                        <span style={{ padding: '1px 7px', borderRadius: 999, background: `${priColor}20`, color: priColor, fontWeight: 600 }}>
                          {action.priority?.replace('_', ' ') || 'open'}
                        </span>
                        {action.owner_name && <span>→ {action.owner_name}</span>}
                        {action.due_date && (
                          <span style={{ color: daysOverdue && daysOverdue > 0 ? RED : DIM }}>
                            {daysOverdue && daysOverdue > 0
                              ? `${daysOverdue}d overdue`
                              : `Due ${new Date(action.due_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}`}
                          </span>
                        )}
                        {action.source && <span>{action.source.replace('_', ' ')}</span>}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
